import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  Button,
} from "@mui/material";
import { PenIcon, PlusIcon } from "../components/svgicons/SvgIcons";
import FancyPaper from "../components/FancyPaper";
import FancyTooltip from "../components/utils/FancyTooltip";
import FancyTablePagination from "../components/utils/FancyTablePagination";
import FancySearch from "../components/utils/FancySearch";
import { useApp } from "../hooks/useApp";
import AddOrUpdateInvoices from "../components/utils/AddOrUpdateInvoices";
import { getInvoicesWithActivities } from "../services/invoices/InvoicesService";
import { getInvoicesWithActivitiesRequestType, InvoiceWithActivitiesContent } from "../services/invoices/InvoicesType";

const formatDateArray = (arr: number[]): string => {
  if (!arr || arr.length < 3) return "";
  const [y, m, d, h = 0, min = 0] = arr;
  return `${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}/${y} ${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
};

const PAGE_SIZE = 10;

const ManagementInvoices = () => {
  const navigate = useNavigate();
  const {
    setLoading,
    setErrorMsg,
    modalData,
    setModalData,
    errorMsg,
  } = useApp();

  const [preFilter, setPreFilter] = useState<string>("");
  const [page, setPage] = useState<number>(0);
  const [invoicesData, setInvoicesData] = useState<getInvoicesWithActivitiesRequestType | null>(null);
  const [formData, setFormData] = useState<InvoiceWithActivitiesContent | null>(null);

  useEffect(() => {
    const dataInit = async () => {
      await handleFetchData("", 0);
    };
    dataInit();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (modalData?.modalOpen && errorMsg) {
      setModalData &&
        setModalData({ modalOpen: false, modalType: "", modalObject: null });
    }
    // eslint-disable-next-line
  }, [errorMsg]);

  const handleFetchData = async (filter: string, currentPage: number) => {
    setLoading && setLoading(true);
    try {
      const data = await getInvoicesWithActivities({ filter, page: currentPage, size: PAGE_SIZE });
      if (data) {
        setInvoicesData(data);
      }
    } catch (error: any) {
      setErrorMsg && setErrorMsg(error.message);
    } finally {
      setLoading && setLoading(false);
    }
  };

  const handleOpenModal = async (event: any) => {
    event.preventDefault();
    const modalAction = event.currentTarget.getAttribute("data-name");
    const index = parseInt(event.currentTarget.getAttribute("data-index"), 10);

    if (modalAction === "view") {
      const found = invoicesData?.content?.[index] ?? null;
      setFormData(found);
    }

    setModalData &&
      setModalData({
        ...modalData,
        modalOpen: true,
        modalType: modalAction,
        modalObject: null,
      });
  };

  const handleCancelModal = () => {
    setFormData(null);
    setModalData &&
      setModalData({ modalOpen: false, modalType: "", modalObject: null });
  };

  const handleChangePage = async (_event: unknown, newPage: number) => {
    const customPage = newPage - 1;
    setPage(customPage);
    if (customPage !== page) {
      await handleFetchData(preFilter, customPage);
    }
  };

  const handleApplyFilter = async () => {
    if (preFilter !== "") {
      await handleFetchData(preFilter, 0);
    }
  };

  return (
    <>
      <FancyPaper
        pagetitle="Listado de facturas"
        prevpage="Inventario"
        linkprevpage=""
      >
        <Grid container spacing={8} sx={{ pb: 8 }}>
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate("/dashboard/uploadXml")}
            >
              Cargar factura
              <PlusIcon sx={{ ml: 1 }} />
            </Button>
          </Grid>
          <Grid
            item
            md={12}
            sm={12}
            xs={12}
            sx={{ display: "flex", justifyContent: "flex-end" }}
          >
            <FancySearch
              preFilter={preFilter}
              setPreFilter={setPreFilter}
              handleFetchData={() => handleFetchData("", 0)}
              handleApplyFilter={handleApplyFilter}
            />
          </Grid>
        </Grid>

        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="invoices-table">
            <TableHead>
              <TableRow>
                <TableCell>Fecha</TableCell>
                <TableCell>N° de factura</TableCell>
                <TableCell>Tipo de movimiento</TableCell>
                <TableCell>Proveedor</TableCell>
                <TableCell>Registrado por</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoicesData?.content?.map((invoice, index) => (
                <TableRow key={index}>
                  <TableCell>{formatDateArray(invoice.createdAt)}</TableCell>
                  <TableCell>{invoice.consecutiveNumber}</TableCell>
                  <TableCell>{invoice.creditNote ? "Nota de crédito" : "Factura electrónica"}</TableCell>
                  <TableCell>{invoice.supplier?.name ?? "-"}</TableCell>
                  <TableCell>{invoice.registeredBy ?? "-"}</TableCell>
                  <TableCell align="center">
                    <FancyTooltip title="Ver detalle" placement="top">
                      <IconButton
                        aria-label="view"
                        component="label"
                        color="primary"
                        onClick={handleOpenModal}
                        data-name="view"
                        data-index={index}
                      >
                        <PenIcon />
                      </IconButton>
                    </FancyTooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Grid container sx={{ mt: 2 }}>
          <Grid item xs={12}>
            <FancyTablePagination
              count={invoicesData?.totalPages ?? 0}
              rowsPerPage={PAGE_SIZE}
              page={page}
              onPageChange={handleChangePage}
              totalElements={invoicesData?.totalElements ?? 0}
              totalPages={invoicesData?.totalPages ?? 0}
            />
          </Grid>
        </Grid>
      </FancyPaper>

      {modalData?.modalOpen && modalData?.modalType === "view" && (
        <Dialog
          open={modalData.modalOpen}
          onClose={handleCancelModal}
          fullWidth
          maxWidth="lg"
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <AddOrUpdateInvoices
            data={formData}
            onSubmit={handleCancelModal}
            cancelModal={handleCancelModal}
            modalType={modalData.modalType}
          />
        </Dialog>
      )}
    </>
  );
};

export default ManagementInvoices;
